// Helpers shared by every entry point that creates an image shape:
// the toolbar picker, the global paste handler, and the drag/drop handler.
// Keeps the size-confirm dialog and FileReader plumbing in one place.

import type * as Y from 'yjs';
import { addShape } from './binding/shapesMap';
import { fitImageSize, makeImage, type WorldPoint } from './tools';

const SIZE_LIMIT_BYTES = 1 * 1024 * 1024; // 1 MB inline gate per scope spec

export interface ImportImageOptions {
  doc: Y.Doc;
  file: File;
  at: WorldPoint;
  authorId: string;
  onCreated?: (id: string) => void;
}

// Reads a `File` as a data URL, optionally confirms big files, decodes the
// image to learn its natural dimensions, sizes it via `fitImageSize`, and
// drops the shape onto the doc at `at`. Returns the new shape id, or `null`
// when the user cancels the size warning or the file fails to decode.
export async function importImage(opts: ImportImageOptions): Promise<string | null> {
  const { doc, file, at, authorId, onCreated } = opts;

  if (file.size > SIZE_LIMIT_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    const ok = window.confirm(
      `This image is ${mb} MB. Storing it inline will slow sync. Continue?`,
    );
    if (!ok) return null;
  }

  const dataUrl = await readAsDataURL(file);
  const natural = await readImageDimensions(dataUrl);
  const size = fitImageSize(natural.w, natural.h);
  const shape = makeImage(at, dataUrl, size, { authorId }, file.name || undefined);
  addShape(doc, shape);
  onCreated?.(shape.id);
  return shape.id;
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function readImageDimensions(src: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    // Bad image → fall back to a square; the placeholder rect will still
    // render and the user can delete it.
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = src;
  });
}

// Find the first image File on a DataTransfer / Clipboard items list. Returns
// null when there isn't one, so the caller can fall through to the default
// behavior (e.g. textareas keep their default paste).
export function findImageFile(items: DataTransferItemList | null | undefined): File | null {
  if (!items) return null;
  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) return file;
    }
  }
  return null;
}
